import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import { HelmetProvider } from 'react-helmet-async';
import { Suspense } from 'react';
import { ROUTES, REDIRECTS, AppLayout } from './RouterConfig';
import ErrorBoundary from '@/components/ErrorBoundary';

// All routes, redirects, guards, and layout flags live in src/RouterConfig.jsx —
// the single source of truth. Do not add <Route> elements here.

const LayoutWrapper = ({ children, currentPageName }) => AppLayout ?
  <AppLayout currentPageName={currentPageName}>{children}</AppLayout>
  : <>{children}</>;

const buildElement = ({ pageName, Component, layout = true, guard: Guard }) => {
  let element = <Component />;
  if (layout) {
    element = <LayoutWrapper currentPageName={pageName}>{element}</LayoutWrapper>;
  }
  if (Guard) {
    element = <Guard>{element}</Guard>;
  }
  return element;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  const location = useLocation();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError && window.location.pathname !== '/Terms') {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
    <motion.div
      key={location.pathname}
      initial={{ x: 24, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -24, opacity: 0 }}
      transition={{ duration: 0.18, ease: "easeInOut" }}
      style={{ minHeight: "100%" }}
    >
    <Suspense fallback={
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    }>
    <Routes location={location}>
      {/* REDIRECTS must render before ROUTES: React Router matches paths
          case-insensitively by default, so a legacy PascalCase redirect
          (e.g. /blog/CAPInspectionReadiness) has the same match rank as the
          real lowercase-slug route (/blog/cap-inspection-readiness). On a
          rank tie, the first Route in this list wins — putting REDIRECTS
          first ensures the old URL actually 30x's instead of silently
          rendering the live page in place (which is what was happening). */}
      {REDIRECTS.map(({ from, to }) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}
      {ROUTES.map((route) => (
        <Route key={route.path} path={route.path} element={buildElement(route)} />
      ))}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
    </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <HelmetProvider>
            <Router>
              <AuthenticatedApp />
            </Router>
          </HelmetProvider>
          <Toaster />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App